import React, { useMemo, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, AlertCircle, FileDown, Printer } from 'lucide-react';
import { useEnhancedPDFGeneration } from '@/hooks/useEnhancedPDFGeneration';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatCurrency, formatPoints } from '../utils/formatters';
import { calculateBuydownScenarios, getDefaultConfiguration } from './utils/buydownCalculations';
import MobileRateSheetCards from './MobileRateSheetCards';

interface RateSheetComparisonProps {
  loanAmount: number;
  purchasePrice: number;
  baseRate: number;
  monthlyTaxes: number;
  monthlyInsurance: number;
  totalClosingCosts: number;
  earnestMoneyDeposit: number;
}

const RateSheetComparison: React.FC<RateSheetComparisonProps> = ({
  loanAmount,
  purchasePrice,
  baseRate,
  monthlyTaxes,
  monthlyInsurance,
  totalClosingCosts,
  earnestMoneyDeposit
}) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { generateEnhancedPDF, isGenerating } = useEnhancedPDFGeneration();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleExportPDF = useCallback(async () => {
    if (!reportRef.current) return;
    
    try {
      await generateEnhancedPDF(reportRef, {
        filename: `rate-sheet-comparison-${new Date().toISOString().split('T')[0]}.pdf`,
        orientation: 'landscape',
        scale: 2,
        quality: 0.95,
        includeMetadata: true,
        reportType: 'Builder Incentive Rate Sheet Comparison',
      });
      
      toast({
        title: "PDF Generated",
        description: "Rate sheet comparison has been downloaded successfully.",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "There was an error generating the PDF. Please try again.",
        variant: "destructive",
      });
    }
  }, [generateEnhancedPDF, toast]);

  const handlePrintSheet = useCallback(() => {
    window.print();
  }, []);
  // Calculate scenarios for all loan types and occupancy combinations
  const allScenarios = useMemo(() => {
    const loanTypes: Array<{
      type: 'conventional' | 'fha' | 'va' | 'usda';
      label: string;
      occupancies: Array<'primary' | 'investment' | 'second'>;
    }> = [
      { type: 'conventional', label: 'Conventional', occupancies: ['primary', 'investment', 'second'] },
      { type: 'fha', label: 'FHA', occupancies: ['primary'] },
      { type: 'va', label: 'VA', occupancies: ['primary'] },
      { type: 'usda', label: 'USDA', occupancies: ['primary'] }
    ];

    const results: any[] = [];

    loanTypes.forEach(({ type, label, occupancies }) => {
      occupancies.forEach((occupancy) => {
        const config = getDefaultConfiguration(type, baseRate, occupancy);
        const scenarios = calculateBuydownScenarios({
          loanAmount,
          loanType: type,
          baseRate,
          purchasePrice,
          downPayment: 0,
          monthlyTaxes,
          monthlyInsurance,
          monthlyHoa: 0,
          monthlyPmi: 0,
          totalClosingCosts,
          earnestMoneyDeposit,
          configuration: config
        });

        const displayLabel = occupancy === 'primary' 
          ? label 
          : `${label} (${occupancy === 'investment' ? 'Investment' : '2nd Home'})`;

        results.push({
          loanType: type,
          occupancy,
          label: displayLabel,
          basePoints: config.fixedPoints,
          investorPoints: config.investorAdditionalPoints || 0,
          scenarios: {
            fixed: scenarios.find(s => s.type === 'fixed'),
            oneZero: scenarios.find(s => s.type === '1/0'),
            twoOne: scenarios.find(s => s.type === '2/1'),
            threeTwoOne: scenarios.find(s => s.type === '3/2/1')
          }
        });
      });
    });

    return results;
  }, [loanAmount, purchasePrice, baseRate, monthlyTaxes, monthlyInsurance, totalClosingCosts, earnestMoneyDeposit]);

  return (
    <div className="space-y-4" ref={reportRef}>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Rate Sheet Comparison - All Loan Types</h3>
          </div>
          <div className="flex gap-2 print:hidden">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportPDF}
              disabled={isGenerating}
            >
              <FileDown className="w-4 h-4 mr-2" />
              {isGenerating ? 'Generating...' : 'Export PDF'}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrintSheet}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
        
        <div className="hidden print:block mb-4 pb-4 border-b">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Generated:</span>
              <span className="ml-2 font-semibold">
                {new Date().toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Loan Amount:</span>
              <span className="ml-2 font-semibold">
                {formatCurrency(loanAmount)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Purchase Price:</span>
              <span className="ml-2 font-semibold">
                {formatCurrency(purchasePrice)}
              </span>
            </div>
          </div>
        </div>

        {isMobile ? (
          <MobileRateSheetCards allScenarios={allScenarios} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-primary">
                  <th className="text-left p-3 font-bold bg-muted">Loan Type</th>
                  <th className="text-center p-3 font-bold bg-blue-50 dark:bg-blue-950">
                    <div>4.99% Fixed</div>
                    <div className="text-xs font-normal text-muted-foreground">30 years</div>
                  </th>
                  <th className="text-center p-3 font-bold bg-green-50 dark:bg-green-950">
                    <div>1/0 Buydown</div>
                    <div className="text-xs font-normal text-muted-foreground">3.99% → 4.99%</div>
                  </th>
                  <th className="text-center p-3 font-bold bg-amber-50 dark:bg-amber-950">
                    <div>2/1 Buydown</div>
                    <div className="text-xs font-normal text-muted-foreground">2.99% → 3.99% → 4.99%</div>
                  </th>
                  <th className="text-center p-3 font-bold bg-purple-50 dark:bg-purple-950">
                    <div>3/2/1 Buydown</div>
                    <div className="text-xs font-normal text-muted-foreground">1.99% → 2.99% → 3.99% → 4.99%</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {allScenarios.map((row, idx) => (
                  <React.Fragment key={`${row.loanType}-${row.occupancy}`}>
                    <tr className={`border-b ${idx % 2 === 0 ? 'bg-muted/30' : ''}`}>
                      <td className="p-3 font-semibold">
                        <div>{row.label}</div>
                        {row.investorPoints > 0 && (
                          <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            +{row.investorPoints} investor pts
                          </div>
                        )}
                      </td>
                      <td className="text-center p-3 bg-blue-50/50 dark:bg-blue-950/30">
                        <div className="space-y-1">
                          <div className="font-mono text-xs text-muted-foreground">
                            {formatPoints(row.scenarios.fixed?.points)} pts
                          </div>
                          <div className="font-bold">
                            {formatCurrency(row.scenarios.fixed?.paymentSchedule[0]?.monthlyPayment || 0)}
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400">
                            {formatCurrency(row.scenarios.fixed?.builderCredit || 0)} credit
                          </div>
                          <div className="text-xs font-semibold">
                            {formatCurrency(row.scenarios.fixed?.cashToClose || 0)} to close
                          </div>
                        </div>
                      </td>
                      <td className="text-center p-3 bg-green-50/50 dark:bg-green-950/30">
                        <div className="space-y-1">
                          <div className="font-mono text-xs text-muted-foreground">
                            {formatPoints(row.scenarios.oneZero?.points)} pts
                          </div>
                          <div className="font-bold">
                            {formatCurrency(row.scenarios.oneZero?.paymentSchedule[0]?.monthlyPayment || 0)}
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400">
                            {formatCurrency(row.scenarios.oneZero?.builderCredit || 0)} credit
                          </div>
                          <div className="text-xs font-semibold">
                            {formatCurrency(row.scenarios.oneZero?.cashToClose || 0)} to close
                          </div>
                        </div>
                      </td>
                      <td className="text-center p-3 bg-amber-50/50 dark:bg-amber-950/30">
                        <div className="space-y-1">
                          <div className="font-mono text-xs text-muted-foreground">
                            {formatPoints(row.scenarios.twoOne?.points)} pts
                          </div>
                          <div className="font-bold">
                            {formatCurrency(row.scenarios.twoOne?.paymentSchedule[0]?.monthlyPayment || 0)}
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400">
                            {formatCurrency(row.scenarios.twoOne?.builderCredit || 0)} credit
                          </div>
                          <div className="text-xs font-semibold">
                            {formatCurrency(row.scenarios.twoOne?.cashToClose || 0)} to close
                          </div>
                        </div>
                      </td>
                      <td className="text-center p-3 bg-purple-50/50 dark:bg-purple-950/30">
                        <div className="space-y-1">
                          <div className="font-mono text-xs text-muted-foreground">
                            {formatPoints(row.scenarios.threeTwoOne?.points)} pts
                          </div>
                          <div className="font-bold">
                            {formatCurrency(row.scenarios.threeTwoOne?.paymentSchedule[0]?.monthlyPayment || 0)}
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400">
                            {formatCurrency(row.scenarios.threeTwoOne?.builderCredit || 0)} credit
                          </div>
                          <div className="text-xs font-semibold">
                            {formatCurrency(row.scenarios.threeTwoOne?.cashToClose || 0)} to close
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isMobile && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg text-xs space-y-2">
            <p className="font-semibold">Notes:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
              <li>All scenarios based on 4.99% fixed rate for 30 years</li>
              <li>Conventional investor/2nd home pricing includes +2.125 points (25% down, 740 credit score)</li>
              <li>Builder credit percentages: Fixed 2%, 1/0 Buydown 1.5%, 2/1 Buydown 1%, 3/2/1 Buydown 0%</li>
              <li>Monthly payments include principal, interest, taxes, and insurance</li>
              <li>Cash to close = Closing costs - Builder credit - Earnest money deposit</li>
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RateSheetComparison;
